import type { ComponentType } from "react";
import {
  BarChartBig,
  Code2,
  Image,
  Link2,
  Percent,
  Sparkles,
  Type,
} from "lucide-react";
import type { StoryWidgetType } from "../../services/dataStories/generateStory.types";

/**
 * Sections (Artifact Configuration) panel, as pure data.
 * Figma: `z2UTps4F1dKNh1bbD4LtYI` node `6515:241171` (header) + the 7 group
 * frames below it (241182 / 75295 / 241220 / 241250 / 242153 / 241232 / 241196).
 *
 * Same contract as `chartFormatSections.ts` / `menus.ts`: structure and copy
 * live here, state and handlers live at the usage site (`useArtifactVisibility`),
 * keyed by `type` / part `id`. Never JSX, never callbacks. Adding a widget type
 * is an entry here — no panel JSX changes.
 *
 * `parts: []` is what makes a row flat (no expand caret, no children) — the row
 * component reads `parts.length`, it never branches on `type`.
 */

/** One sub-element toggle under a widget row (e.g. a chart's "AI summary"). */
export interface ArtifactSectionPart {
  /** Stable id, namespaced under the parent widget id at the usage site. */
  id: string;
  label: string;
}

export interface ArtifactSectionTemplate {
  /**
   * `StoryWidgetType` covers every widget the canvas can render today.
   * `"anticipatedQuestions"` has no widget yet (see below) — the entry exists
   * so the group is a one-line addition once that widget ships, not a rewrite.
   */
  type: StoryWidgetType | "anticipatedQuestions";
  /** Section-group label ("Charts", "KPI cards"). */
  groupLabel: string;
  /** 16px row icon. lucide-compatible signature so any icon set can slot in. */
  icon: ComponentType<{ size?: number | string }>;
  /** Sub-elements shown as indented child rows when the parent is expanded. */
  parts: readonly ArtifactSectionPart[];
}

/**
 * Registry order is also the panel's group order (matches the Figma stack:
 * AI intelligence, KPI cards, Charts, Links, Embedded chart, Images, Text box).
 */
export const artifactSectionTemplates: readonly ArtifactSectionTemplate[] = [
  // Story-level AI cards — Figma groups these under one "AI intelligence"
  // header. Flat: Figma shows no caret/children on this group.
  {
    type: "summary",
    groupLabel: "AI intelligence",
    icon: Sparkles,
    parts: [],
  },
  {
    type: "narrative",
    groupLabel: "AI intelligence",
    icon: Sparkles,
    parts: [],
  },
  // Not yet a real widget in generateStory.types.ts — registry entry ships
  // ready so the group gains this row without a template rewrite.
  {
    type: "anticipatedQuestions",
    groupLabel: "AI intelligence",
    icon: Sparkles,
    parts: [],
  },
  {
    type: "kpi",
    groupLabel: "KPI cards",
    icon: Percent,
    parts: [
      { id: "kpiCard", label: "KPI card" },
      { id: "aiNarrative", label: "AI narrative" },
    ],
  },
  {
    type: "chart",
    groupLabel: "Charts",
    icon: BarChartBig,
    parts: [
      { id: "chart", label: "Chart" },
      { id: "aiSummary", label: "AI summary" },
      { id: "aiNarrative", label: "AI narrative" },
    ],
  },
  // Same widget shape as chart (companion sections), grouped with it.
  {
    type: "insight",
    groupLabel: "Charts",
    icon: BarChartBig,
    parts: [],
  },
  {
    type: "link",
    groupLabel: "Links",
    icon: Link2,
    parts: [],
  },
  // Same widget shape for all three "how it got here" tags — one group.
  {
    type: "report",
    groupLabel: "Embedded chart",
    icon: Code2,
    parts: [],
  },
  {
    type: "ingested_widget",
    groupLabel: "Embedded chart",
    icon: Code2,
    parts: [],
  },
  {
    type: "captured_widget",
    groupLabel: "Embedded chart",
    icon: Code2,
    parts: [],
  },
  {
    type: "embed",
    groupLabel: "Embedded chart",
    icon: Code2,
    parts: [],
  },
  {
    type: "image",
    groupLabel: "Images",
    icon: Image,
    parts: [],
  },
  {
    type: "text",
    groupLabel: "Text box",
    icon: Type,
    parts: [],
  },
] as const;
